import {useQueryClient} from '@tanstack/react-query';
import {chain} from 'lodash';
import React, {FC, ReactNode, useEffect, useState} from 'react';
import {styled} from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {AsyncStatusesEnum} from '../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {parseUniqueEmailsFromText} from '../../../../../libs/shared/core/src/helpers/utilities/stringHelpers';
import {LicensesStatusesEnum} from '../../../../../libs/shared/core/src/models/api/billing/billingModel';
import {globalNamespace} from '../../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {DataEventActionsEnum} from '../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {OnboardingDataEventObject} from '../../../../../libs/shared/core/src/models/tracking/objectNames/onboardingDataEventObject';
import {postAsync} from '../../../../../libs/shared/core/src/services/http/http';
import {useDataEventTracking} from '../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {
  InlineBanner,
  InlineBannerTypesEnum,
} from '../../../../../libs/shared/ui/src/components/banners/inlineBanner/inlineBanner';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Link} from '../../../../../libs/shared/ui/src/components/links/link';
import {useLinkNavigation} from '../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {SpecialRoleAliasesEnum} from '../../../../models/api/roleModel';
import {SettingsTeammatesResourcePagesEnum} from '../../../../models/url/models/teammatesUrlModel';
import {SettingsRootPagesEnum} from '../../../../models/url/models/types';
import {buildSettingsPath} from '../../../../models/url/settingsUrlHelpers';
import {getBillingInviteTeammatesProps} from '../../../../settings/billing/common/plans/billingPlanLimitsHelpers';
import {useFetchBillingLicensesStatus, useFetchTeammatesUsage} from '../../../../settings/fetchers/billing';
import {
  fetchAccessibleInboxesForTeammateQuery,
  fetchTeamInboxesQuery,
} from '../../../../settings/fetchers/inboxes';
import {fetchTags} from '../../../../settings/fetchers/tags';
import {fetchTeammatePreferencesQuery} from '../../../../settings/fetchers/teammates';
import {fetchWorkspace} from '../../../../settings/fetchers/workspaces';
import {buildComputeInviteTeammatesPayload} from '../../../../settings/teammates/helpers/computeInviteTeammatePayload';
import {NamespaceIds} from '../../../../settings/teammates/helpers/computePayloadHelpers';
import {InviteTeammatesBillingBanner} from '../../../../settings/teammates/pages/settingsTeammatesBillingBanner';
import {LicensesUnavailableInfoBox} from '../../../../settings/teammates/pages/settingsTeammatesNewEnterEmail';
import {getCompany, getIsCustomer, getIsInTrial} from '../../../../store/data/company/companySelectors';
import {getFlatCircles} from '../../../../store/data/dataSelectors';
import {getFlatTeams, makeGetNamespaceTeams} from '../../../../store/data/teams/teamsSelectors';
import {getBillingDetails} from '../../../../store/metadata/billing/billingSelectors';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {EmailsInput, EmailsInputState, OnChangeEmails} from '../../../common/editor/tokens/emailsInput';
import {OnboardingGuidePopover, OnboardingGuideRenderer} from '../../guide/onboardingGuidePopover';
import {OnboardingGuideSlide} from '../../guide/onboardingGuideSlide';
import {
  StyledOnboardingFormTitleDiv,
  StyledOnboardingFormTitlesDiv,
} from '../../welcome/onboardingWelcomeHelpers';
import {QuickInviteAdminToggle} from './quickInviteAdminToggle';
import {QuickInvitePopoverVisual} from './quickInvitePopoverVisual';

/*
 * Props.
 */

export interface QuickInvitePopoverProps {
  onClose: () => void;
  renderQuickInviteTransition: (render: OnboardingGuideRenderer) => ReactNode;
  isQuickInvitePopoverOpen: boolean;
  emails: EmailsInputState;
  onChangeEmails: OnChangeEmails;
  isGlobalAdmin: boolean;
  onChangeIsGlobalAdmin: (setIsGlobalAdmin: (isGlobalAdmin: boolean) => boolean) => void;
}

/*
 * Style.
 */

const StyledLeftContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 456px;
`;

const StyledNavDiv = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
`;

const StyledDetailsTextDiv = styled.div`
  border-top: 1px solid ${(p) => p.theme.separator.border};
  padding-top: 24px;
  font-size: ${fontSizes.medium};
  color: ${(p) => p.theme.greys.shade70};
`;

/*
 * Component.
 */

export const QuickInvitePopover: FC<QuickInvitePopoverProps> = (props) => {
  const {
    onClose,
    renderQuickInviteTransition,
    isQuickInvitePopoverOpen,
    emails,
    onChangeEmails,
    isGlobalAdmin,
    onChangeIsGlobalAdmin,
  } = props;

  const dispatch = useAppDispatch();
  const {trackDataEvent} = useDataEventTracking();
  const client = useQueryClient();
  const {navigateToLink} = useLinkNavigation();

  const billingDetails = useAppSelector(getBillingDetails);
  const {data: teammatesUsage} = useFetchTeammatesUsage();
  const licensesStatus = useFetchBillingLicensesStatus();

  const company = useAppSelector(getCompany);
  const allTeams = useAppSelector(getFlatTeams);
  const circles = useAppSelector(getFlatCircles);
  const teams = useAppSelector(makeGetNamespaceTeams(globalNamespace));
  const isInTrial = useAppSelector(getIsInTrial);
  const isCustomer = useAppSelector(getIsCustomer);

  const [inviteStatus, setInviteStatus] = useState<AsyncStatusesEnum>(AsyncStatusesEnum.LOADED);

  const {seatLimitType} = getBillingInviteTeammatesProps(
    billingDetails?.plan || undefined,
    isInTrial,
    teammatesUsage,
    emails.tokens.length,
  );

  const advancedTeammateSettingsUrl = buildSettingsPath({
    rootPage: SettingsRootPagesEnum.TEAMMATES,
    resourcePage: SettingsTeammatesResourcePagesEnum.NEW,
    namespace: globalNamespace,
    subPage: null,
  });

  const [emailTextPendingTokenization] = parseUniqueEmailsFromText(emails.text);
  const canSubmitEmails = Boolean(emails.tokens.length || emailTextPendingTokenization.length);

  const onCancel = () => {
    trackDataEvent(DataEventActionsEnum.CLICK, OnboardingDataEventObject.QUICK_INVITE_POPOVER_CANCEL);
    onClose();
  };

  const onNavigateToBilling = () => {
    trackDataEvent(
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.QUICK_INVITE_POPOVER_UPGRADE_PLAN_BILLING_CTA,
    );
    onClose();
    dispatch(navigateToLinkAction('/settings/billing'));
  };

  const onClickAddMoreTeammatesBillingCta = () => {
    trackDataEvent(
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.QUICK_INVITE_POPOVER_ADD_MORE_TEAMMATES_BILLING_CTA,
    );
    onClose();
  };

  const onClickAdvancedTeammateSettingsCta = () => {
    trackDataEvent(
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.QUICK_INVITE_POPOVER_ADVANCED_TEAMMATE_SETTINGS_CTA,
    );
    onClose();
  };

  const onInvite = async () => {
    try {
      setInviteStatus(AsyncStatusesEnum.LOADING);

      const [emailsPendingTokenization] = parseUniqueEmailsFromText(emails.text);
      const emailTokens = [...new Set([...emails.tokens, ...emailsPendingTokenization])];

      if (!emailTokens.length) {
        setInviteStatus(AsyncStatusesEnum.LOADED);
        return;
      }

      const computePayload = buildComputeInviteTeammatesPayload(
        allTeams,
        (team) => fetchTeamInboxesQuery(client, company, team),
        (teammate) => fetchAccessibleInboxesForTeammateQuery(client, company, teammate),
        (teammate) => fetchTeammatePreferencesQuery(client, teammate),
        (teammate) => fetchWorkspace(client, teammate),
        (idsToFetch) => fetchTags(idsToFetch, company),
      );

      const teammateSpecs = emailTokens.map((email) => ({
        email,
        givenName: '',
        familyName: '',
      }));

      const inboxAccessAndVisibilityAccess = chain(teams)
        .filter((t) => !t.isSandbox || isGlobalAdmin)
        .keyBy((t) => t.namespace)
        .mapValues(() => ({
          role: isGlobalAdmin ? SpecialRoleAliasesEnum.ADMIN : SpecialRoleAliasesEnum.MEMBER,
          inbox_ids: [],
        }))
        .value();

      const visibleInboxes: NamespaceIds = {};
      for (const team of teams) {
        if (!team.isSandbox || isGlobalAdmin) {
          const teamInboxes = await fetchTeamInboxesQuery(client, company, team);
          visibleInboxes[team.namespace] = [...new Set(teamInboxes.map((t) => t.id))];
        }
      }

      // The quick invite flow has very relaxed permissions, inviting everyone to everything.
      const payload = await computePayload(
        isGlobalAdmin,
        teammateSpecs,
        inboxAccessAndVisibilityAccess,
        null,
        [],
        false,
        visibleInboxes,
        circles.map((circle) => circle.id),
      );

      await postAsync(`${company.url}/mass_invite`, payload);

      onChangeEmails(() => ({text: '', tokens: []}));
      onChangeIsGlobalAdmin(() => false);

      trackDataEvent(DataEventActionsEnum.CLICK, OnboardingDataEventObject.QUICK_INVITE_POPOVER_INVITE, {
        teammate_count: emailTokens.length,
        is_global_admin: isGlobalAdmin,
      });

      navigateToLink(
        buildSettingsPath({
          rootPage: SettingsRootPagesEnum.TEAMMATES,
          resourcePage: SettingsTeammatesResourcePagesEnum.LIST,
          namespace: globalNamespace,
        }),
      );

      onClose();
    } catch (err) {
      setInviteStatus(AsyncStatusesEnum.FAILED);
      return;
    }

    setInviteStatus(AsyncStatusesEnum.LOADED);
  };

  useEffect(() => {
    if (!isQuickInvitePopoverOpen) {
      return;
    }

    trackDataEvent(DataEventActionsEnum.VIEW, OnboardingDataEventObject.QUICK_INVITE_POPOVER_VIEW);
  }, [isQuickInvitePopoverOpen, trackDataEvent]);

  return renderQuickInviteTransition((styles) => (
    <OnboardingGuidePopover onRequestClose={() => onCancel()} styles={styles}>
      <OnboardingGuideSlide
        navPrimary={
          <StyledNavDiv>
            <Button variant={ButtonVariantsEnum.SECONDARY} size={VisualSizesEnum.MEDIUM} onClick={onCancel}>
              <Trans id="quick-invite-popover-nav-cancel">Cancel</Trans>
            </Button>
            <Button
              variant={ButtonVariantsEnum.PRIMARY}
              size={VisualSizesEnum.MEDIUM}
              onClick={onInvite}
              isDisabled={!canSubmitEmails || inviteStatus === AsyncStatusesEnum.LOADING}
            >
              <Trans id="quick-invite-popover-nav-invite">Invite</Trans>
            </Button>
          </StyledNavDiv>
        }
        isLeftContent
      >
        <StyledLeftContentWrapper>
          <StyledOnboardingFormTitlesDiv>
            <StyledOnboardingFormTitleDiv>
              <Trans id="quick-invite-popover-title">Invite your team</Trans>
            </StyledOnboardingFormTitleDiv>
          </StyledOnboardingFormTitlesDiv>

          {/* Seats are not purchasable due to limitation */}
          <InviteTeammatesBillingBanner seatLimitType={seatLimitType} onUpgradePlan={onNavigateToBilling} />

          {/* Seats are purchasable by inviting more teammates */}
          {!seatLimitType && (
            <LicensesUnavailableInfoBox
              isShowing={isCustomer && licensesStatus?.full === LicensesStatusesEnum.FILLED}
              namespace={globalNamespace}
              onClickCta={() => onClickAddMoreTeammatesBillingCta()}
            />
          )}

          {inviteStatus === AsyncStatusesEnum.FAILED && (
            <InlineBanner
              type={InlineBannerTypesEnum.ERROR}
              title={
                <Trans id="quick-invite-popover-invite-error">
                  An error occurred while trying to invite your teammates.
                </Trans>
              }
              onClose={() => setInviteStatus(AsyncStatusesEnum.LOADED)}
            />
          )}

          <EmailsInput emails={emails} onChangeEmails={onChangeEmails} />

          <QuickInviteAdminToggle
            value={isGlobalAdmin}
            onToggle={() => onChangeIsGlobalAdmin((prevIsGlobalAdmin) => !prevIsGlobalAdmin)}
          />

          <StyledDetailsTextDiv>
            <Trans id="quick-invite-popover-details">
              By default, invited teammates will have access to all inboxes and teammate groups. You can
              configure access in{' '}
              <Link to={advancedTeammateSettingsUrl} onClick={() => onClickAdvancedTeammateSettingsCta()}>
                advanced settings
              </Link>
              .
            </Trans>
          </StyledDetailsTextDiv>
        </StyledLeftContentWrapper>
      </OnboardingGuideSlide>
      <QuickInvitePopoverVisual invitedTeammateHandles={emails.tokens} />
    </OnboardingGuidePopover>
  ));
};
